/**
 * Simple editor for the 3 lists (Régies, Partenaires financiers, Sous-traitants).
 * Backed by /api/configuration/lists/{key}. Add / edit / delete / reorder.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";
import AppLayout, { PageHeader } from "../components/AppLayout";
import { api } from "../lib/api";
import TagFamiliesEditor from "../components/TagFamiliesEditor";
import RavThresholdsEditor from "../components/RavThresholdsEditor";

const LISTS = [
  { key: "partenaires-financiers", label: "Partenaires financiers", helper: "Organismes financeurs (ex. Sofinco, Projexio, Domofinance…)." },
];

function ListEditor({ listDef }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/configuration/lists/${listDef.key}`);
      setItems(r.data.items || []);
    } finally {
      setLoading(false);
    }
  }, [listDef.key]);

  useEffect(() => { load(); }, [load]);

  const add = () => {
    const name = draft.trim();
    if (!name) return;
    if (items.find((i) => i.name.toLowerCase() === name.toLowerCase())) {
      toast.error("Doublon ignoré");
      return;
    }
    setItems((prev) => [...prev, { id: null, name, meta: {} }]);
    setDraft("");
  };

  const removeAt = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const rename = (idx, name) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, name } : it)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = items.map((it) => ({ id: it.id, name: it.name, meta: it.meta || {} }));
      await api.put(`/configuration/lists/${listDef.key}`, { items: payload });
      await load();
      toast.success("Liste enregistrée");
    } catch {
      toast.error("Échec de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-flat p-5" data-testid={`list-editor-${listDef.key}`}>
      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <h3 className="font-heading text-lg font-medium text-norea-text">{listDef.label}</h3>
          <p className="text-xs text-norea-textMuted mt-0.5">{listDef.helper}</p>
        </div>
        <button
          onClick={save}
          disabled={saving || loading}
          className="btn-primary text-xs"
          data-testid={`btn-save-${listDef.key}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          Enregistrer
        </button>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Ajouter un élément…"
          className="flex-1 px-3 py-1.5 text-sm border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary focus:border-norea-primary outline-none"
          data-testid={`input-add-${listDef.key}`}
        />
        <button
          onClick={add}
          className="btn-secondary text-xs"
          data-testid={`btn-add-${listDef.key}`}
        >
          <Plus className="w-3 h-3" /> Ajouter
        </button>
      </div>

      {loading ? (
        <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-xs text-norea-textMuted italic">Liste vide — ajoute un premier élément ci-dessus.</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it, idx) => (
            <li
              key={idx}
              className="flex items-center gap-2 p-2 rounded border border-norea-border bg-white"
              data-testid={`list-item-${listDef.key}-${idx}`}
            >
              <span className="text-xs text-norea-textMuted font-mono w-6">{idx + 1}.</span>
              <input
                value={it.name}
                onChange={(e) => rename(idx, e.target.value)}
                className="flex-1 text-sm bg-transparent focus:outline-none"
                data-testid={`list-input-${listDef.key}-${idx}`}
              />
              <button
                onClick={() => removeAt(idx)}
                className="text-norea-textMuted hover:text-red-600"
                title="Supprimer"
                data-testid={`list-delete-${listDef.key}-${idx}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function SettingsListsPage() {
  return (
    <AppLayout>
      <PageHeader
        title="Listes de référence"
        subtitle="Gère les listes utilisées dans les champs des dossiers (combobox, dropdowns)."
        breadcrumb={[
          { label: "Norea", to: "/" },
          { label: "Configuration", to: "/configuration" },
          "Listes de référence",
        ]}
      />
      <div className="flex items-center gap-2 mb-4 text-xs text-norea-textMuted">
        <ListChecks className="w-3.5 h-3.5" />
        <span>Les éléments ajoutés ici sont visibles immédiatement dans les formulaires des dossiers.</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {LISTS.map((l) => <ListEditor key={l.key} listDef={l} />)}
        <RavThresholdsEditor />
        <TagFamiliesEditor />
      </div>
    </AppLayout>
  );
}
